import { useMemo } from 'react';
import { Box, Flex, Text, VStack, useColorModeValue } from '@chakra-ui/react';

import type { QuoteGroupSummaryQuote } from 'api/quote-marketplace';
import { formatCurrency } from '../../constants';

interface CombinedPremiumSummaryProps {
  quotes: QuoteGroupSummaryQuote[];
  combinedAnnualPremium: string | null;
  combinedMonthlyPremium: string | null;
}

interface AggregatedPremium {
  lineOfBusiness: number;
  lineOfBusinessDisplay: string;
  annualPremium: number;
  monthlyPremium: number;
}

const CombinedPremiumSummary = ({
  quotes,
  combinedAnnualPremium,
  combinedMonthlyPremium,
}: CombinedPremiumSummaryProps) => {
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const combinedBg = useColorModeValue('blue.50', 'blue.900');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const aggregatedByLob = useMemo((): AggregatedPremium[] => {
    const quotesWithCarrier = quotes.filter((q) => q.has_carrier);
    const lobMap = new Map<number, AggregatedPremium>();

    for (const quote of quotesWithCarrier) {
      const existing = lobMap.get(quote.line_of_business);
      const annual = parseFloat(quote.annual_premium || '0') || 0;
      const monthly = parseFloat(quote.monthly_premium || '0') || 0;

      if (existing) {
        existing.annualPremium += annual;
        existing.monthlyPremium += monthly;
      } else {
        lobMap.set(quote.line_of_business, {
          lineOfBusiness: quote.line_of_business,
          lineOfBusinessDisplay: quote.line_of_business_display,
          annualPremium: annual,
          monthlyPremium: monthly,
        });
      }
    }

    return Array.from(lobMap.values());
  }, [quotes]);

  const hasMonthlyPremiums = aggregatedByLob.some(
    (lob) => lob.monthlyPremium > 0,
  );

  return (
    <VStack spacing={4} align="stretch" mt={4}>
      {hasMonthlyPremiums && combinedMonthlyPremium && (
        <Box
          bg={combinedBg}
          p={4}
          borderRadius="md"
          borderWidth="1px"
          borderColor={borderColor}
        >
          <Flex justify="space-between" align="center" mb={3}>
            <Text fontWeight="600">
              Total Estimated Monthly Combined Premium
            </Text>
            <Text fontSize="xl" fontWeight="700">
              {formatCurrency(combinedMonthlyPremium)}
            </Text>
          </Flex>
          <VStack spacing={1} align="stretch">
            {aggregatedByLob.map((lob) => (
              <Flex
                key={`monthly-${lob.lineOfBusiness}`}
                justify="space-between"
              >
                <Text fontSize="sm" color={labelColor}>
                  Total Estimated Monthly {lob.lineOfBusinessDisplay} Premium
                </Text>
                <Text fontSize="sm" color={labelColor}>
                  {formatCurrency(String(lob.monthlyPremium.toFixed(2)))}
                </Text>
              </Flex>
            ))}
          </VStack>
        </Box>
      )}

      <Box
        bg={combinedBg}
        p={4}
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Flex justify="space-between" align="center" mb={3}>
          <Text fontWeight="600">Total Estimated Combined Premium</Text>
          <Text fontSize="xl" fontWeight="700">
            {formatCurrency(combinedAnnualPremium)}
          </Text>
        </Flex>
        <VStack spacing={1} align="stretch">
          {aggregatedByLob.map((lob) => (
            <Flex key={`annual-${lob.lineOfBusiness}`} justify="space-between">
              <Text fontSize="sm" color={labelColor}>
                Total Estimated {lob.lineOfBusinessDisplay} Premium
              </Text>
              <Text fontSize="sm" color={labelColor}>
                {formatCurrency(String(lob.annualPremium.toFixed(2)))}
              </Text>
            </Flex>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
};

export default CombinedPremiumSummary;
